import { useRef, useState } from "react";
import { OrderItem } from "../models/IOrders";
import { submitFeedback } from "../services/feedbackService";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

interface Props {
  order: OrderItem | null;
  restaurantName: string;
  onSuccess: (orderId: string) => void;
  id?: string;
}

function StarRating({
  value,
  onChange,
  label,
}: {
  value: number;
  onChange: (v: number) => void;
  label: string;
}) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="mb-3">
      <div className="small fw-semibold mb-1">{label}</div>
      <div className="d-flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <i
            key={star}
            className={`bi ${
              (hovered || value) >= star
                ? "bi-star-fill text-warning"
                : "bi-star text-muted"
            }`}
            style={{ fontSize: "1.6rem", cursor: "pointer" }}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
          />
        ))}
      </div>
    </div>
  );
}

function FeedbackModal({ order, restaurantName, onSuccess, id = "feedbackModal" }: Props) {
  const { t } = useLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [speedPoint, setSpeedPoint] = useState(0);
  const [servicePoint, setServicePoint] = useState(0);
  const [tastePoint, setTastePoint] = useState(0);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setSpeedPoint(0);
    setServicePoint(0);
    setTastePoint(0);
    setDescription("");
    setError("");
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!order) return;
    if (speedPoint === 0 || servicePoint === 0 || tastePoint === 0) {
      setError(t("feedback.ratingRequired"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      await submitFeedback({
        orderId: order.id,
        restourantId: order.preOrder.restourantId!,
        feedbackSpeedPoint: speedPoint,
        feedbackServicePoint: servicePoint,
        feedbackTeastePoint: tastePoint,
        feedbackDescription: description || undefined,
      });
      setSubmitted(true);
      onSuccess(order.id);
      setTimeout(() => {
        closeRef.current?.click();
        reset();
      }, 1800);
    } catch (err) {
      setError(getApiErrorMessage(err, t("feedback.error")));
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    closeRef.current?.click();
    reset();
  };

  return (
    <div
      className="modal fade"
      id={id}
      tabIndex={-1}
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        className="modal-dialog modal-dialog-centered"
        style={{ maxWidth: "420px" }}
      >
        <div className="modal-content rounded-4 shadow">
          <div className="modal-body px-4 py-4">
            <button ref={closeRef} className="d-none" data-bs-dismiss="modal" />

            {submitted ? (
              <div className="text-center py-3">
                <i
                  className="bi bi-check-circle-fill text-success"
                  style={{ fontSize: "3rem" }}
                ></i>
                <p className="fw-bold mt-3 mb-0">{t("feedback.thankYou")}</p>
              </div>
            ) : (
              <>
                <div className="text-center mb-4">
                  <i
                    className="bi bi-star-fill text-warning"
                    style={{ fontSize: "2rem" }}
                  ></i>
                  <h6 className="fw-bold fs-5 mt-2 mb-0">
                    {t("feedback.title")}
                  </h6>
                  {restaurantName && (
                    <div className="text-muted small mt-1">
                      <i className="bi bi-shop me-1"></i>
                      {restaurantName}
                    </div>
                  )}
                </div>

                <StarRating
                  value={speedPoint}
                  onChange={setSpeedPoint}
                  label={t("feedback.speed")}
                />
                <StarRating
                  value={servicePoint}
                  onChange={setServicePoint}
                  label={t("feedback.service")}
                />
                <StarRating
                  value={tastePoint}
                  onChange={setTastePoint}
                  label={t("feedback.taste")}
                />

                <div className="mb-3">
                  <label className="small fw-semibold mb-1">
                    {t("feedback.comment")}
                  </label>
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    maxLength={1000}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={t("feedback.commentPlaceholder")}
                  />
                </div>

                {error && (
                  <div className="alert alert-danger py-2 small mb-3">
                    <i className="bi bi-x-circle me-1"></i>
                    {error}
                  </div>
                )}

                <div className="d-flex gap-2 mt-1">
                  <button
                    type="button"
                    className="btn btn-outline-secondary flex-grow-1 rounded-pill"
                    onClick={handleSkip}
                    disabled={loading}
                  >
                    {t("feedback.skip")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger flex-grow-1 rounded-pill"
                    onClick={handleSubmit}
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm me-1" />
                    ) : (
                      <i className="bi bi-send me-1"></i>
                    )}
                    {t("feedback.submit")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;
